/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useCallback } from "react";
import ReactECharts from "src/utils/EChartsReact";
import { formatNumber, convertStringToNumeric } from "src/pages/Insights/transformer/dashboardUtils";
import { getCommonTooltipConfig, buildChartTooltip, useTooltipCoordination } from "./../../BarCharts/tooltipFormatter";
import { hasAnyStatMarkers, resolveStatMarkerRows, buildVLineMarkPoints } from "../../components/StatMarkerUtils";
import { chartConfig } from "./../../chartConfig";
import { IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { useScaledConfig } from "../../ChartScaleContext";
import { getNSizeStatusLabel, NSizeLabelText, SUPPRESSED_VALUE_PLACEHOLDER, SUPPRESSED_VALUE_COLOR } from "../../BarCharts/chartHelpers";

/* -------------------- Types -------------------- */
type WaveDataPoint = {
	wave: string;
	value: number;
	n_size?: number | string;
	stat_marker?: IStatMarker | null;
};

interface IDataPoint {
	seriesIndex: number;
	dataIndex: number;
	value: number;
}

/* -------------------- Overlap Detection Helper -------------------- */
const buildOverlapMap = (
	filteredProducts: string[],
	filteredAttributes: string[],
	productData: Record<string, Record<string, WaveDataPoint[]>>,
	isPercentage: boolean,
	threshold: number = 5,
): Set<string> => {
	const overlapSet = new Set<string>();
	const reversedAttributes = [...filteredAttributes].reverse();

	const pointsByPosition = new Map<number, IDataPoint[]>();

	filteredProducts.forEach((product, seriesIndex) => {
		reversedAttributes.forEach((attribute, dataIndex) => {
			const waveData = productData[product]?.[attribute]?.[0];

			if (waveData?.value != null) {
				const value = isPercentage ? waveData.value * 100 : waveData.value;

				if (!pointsByPosition.has(dataIndex)) {
					pointsByPosition.set(dataIndex, []);
				}
				pointsByPosition.get(dataIndex)!.push({ seriesIndex, dataIndex, value });
			}
		});
	});

	pointsByPosition.forEach((points) => {
		if (points.length <= 1) return;

		points.sort((a, b) => b.value - a.value);

		for (let i = 1; i < points.length; i++) {
			if (points[i - 1].value - points[i].value <= threshold) {
				overlapSet.add(`${points[i].seriesIndex}-${points[i].dataIndex}`);
			}
		}
	});

	return overlapSet;
};

interface IMiniVLineChartProps {
	attributes: string[];
	width?: number;
	height: number;
	labelFormat: string;
	labelOverlapThreshold?: number;
	lineWeight?: number;
	productColors: Record<string, string>;
	productData: Record<string, Record<string, WaveDataPoint[]>>;
	smoothLine?: boolean;
	visibleProducts: string[];
	visibleWaves: string[];
	waveMarkers: Record<string, string>;
	xAxisDomain: { min: number; max: number };
	axisRight: number;
	axisLeft?: number;
	statTestConfig?: IStatTestConfig | null;
}

/* -------------------- Marker Symbol Mapping -------------------- */
const getMarkerSymbol = (markerType: string): string => {
	const markerMap: Record<string, string> = {
		Square: "rect",
		Triangle: "triangle",
		Circle: "circle",
		Cross: "path://M-5,-5L5,5M5,-5L-5,5",
		Diamond: "diamond",
		Pin: "pin",
		Arrow: "arrow",
	};
	return markerMap[markerType] || "circle";
};

/* -------------------- Component -------------------- */
const MiniVLineChart: React.FC<IMiniVLineChartProps> = ({
	attributes,
	width = 300,
	height,
	labelFormat,
	labelOverlapThreshold = 10,
	lineWeight = 1,
	productColors,
	productData,
	smoothLine = false,
	visibleProducts,
	visibleWaves,
	waveMarkers,
	xAxisDomain,
	axisRight,
	axisLeft = 0,
	statTestConfig = null,
}) => {
	const scaled = useScaledConfig();
	const isPercentage = labelFormat.includes("%");

	const { chartRef, onEvents } = useTooltipCoordination();

	const config = useMemo(
		() => ({
			labelFontSize: scaled.typography.dataLabel,
			markerSize: scaled.geometry.markerRadius * 2,
			scaledLineWeight: lineWeight * scaled.geometry.strokeWidth,
		}),
		[scaled, lineWeight],
	);

	const filteredAttributes = useMemo(() => {
		return attributes.filter((attr) => visibleWaves.includes(attr));
	}, [attributes, visibleWaves]);

	const filteredProducts = useMemo(() => {
		const allProducts = Object.keys(productData);
		return allProducts.filter((p) => p === "_default" || visibleProducts.includes(p));
	}, [productData, visibleProducts]);

	const overlapMap = useMemo(() => {
		return buildOverlapMap(filteredProducts, filteredAttributes, productData, isPercentage, labelOverlapThreshold);
	}, [filteredProducts, filteredAttributes, productData, isPercentage, labelOverlapThreshold]);

	const hasAnyMarkers = useMemo(() => {
		if (!statTestConfig?.enabled) return false;
		return filteredProducts.some((product) =>
			filteredAttributes.some((attr) => {
				const waveData = productData[product]?.[attr]?.[0];
				return waveData?.stat_marker ? hasAnyStatMarkers([{ stat_marker: waveData.stat_marker }], statTestConfig) : false;
			}),
		);
	}, [filteredProducts, filteredAttributes, productData, statTestConfig]);

	const flatSeriesForTooltip = useMemo(() => {
		const items: { name: string; value: number | string; n_size: number | string; stat_marker?: IStatMarker | null }[] = [];
		filteredProducts.forEach((product) => {
			filteredAttributes.forEach((attr) => {
				const waveData = productData[product]?.[attr]?.[0];
				if (waveData) {
					items.push({
						name: product,
						value: waveData.value,
						n_size: waveData.n_size ?? "No Data",
						stat_marker: waveData.stat_marker ?? null,
					});
				}
			});
		});
		return items;
	}, [filteredProducts, filteredAttributes, productData]);

	const tooltipFormatter = useCallback(
		(params: any) => buildChartTooltip(params, flatSeriesForTooltip, labelFormat, statTestConfig, productColors),
		[flatSeriesForTooltip, labelFormat, statTestConfig, productColors],
	);

	const echartsSeries = useMemo(() => {
		const series: any[] = [];
		const reversedAttributes = [...filteredAttributes].reverse();
		const LABEL_FONT_SIZE = config.labelFontSize;

		filteredProducts.forEach((product) => {
			const color = productColors[product];
			const markerSymbol = getMarkerSymbol(waveMarkers[product] || "Circle");

			const markPointData: any[] = [];

			const seriesData = reversedAttributes.map((attribute, dataIndex) => {
				const waveData = productData[product]?.[attribute] || [];
				const dataPoint = waveData[0];

				if (!dataPoint || dataPoint.value === null || dataPoint.value === undefined) {
					return null;
				}

				const nSizeStatusLabel = getNSizeStatusLabel(dataPoint.n_size);
				const suppressValue = nSizeStatusLabel !== null;

				// Low n-size / no-data n_size: treat exactly like missing data — do not plot the
				// point (it would otherwise render at 0 as a misleading dip). connectNulls bridges
				// the gap; the n-size is still surfaced in the N-Size header row and the tooltip.
				if (suppressValue) {
					return null;
				}

				const processedValue = isPercentage ? dataPoint.value * 100 : dataPoint.value;
				const formattedVal = suppressValue ? nSizeStatusLabel! : formatNumber(dataPoint.value, labelFormat);
				const value = suppressValue ? 0 : convertStringToNumeric(formattedVal);

				const seriesIdx = filteredProducts.indexOf(product);
				const isOverlapHidden = overlapMap.has(`${seriesIdx}-${dataIndex}`);

				const resolvedRows = statTestConfig?.enabled && dataPoint.stat_marker ? resolveStatMarkerRows(dataPoint.stat_marker, statTestConfig) : [];

				// const hasMarker = resolvedRows.length > 0;
				// const markerBorderColor = resolvedRows[0]?.style?.font_color || "#DC2626";

				if (resolvedRows.length > 0 && !isOverlapHidden && statTestConfig && !suppressValue) {
					const dataLabelHalfHeight = Math.ceil(LABEL_FONT_SIZE / 2);
					const availableForMarker = width * 0.3;
					const points = buildVLineMarkPoints(resolvedRows, statTestConfig!, value, attribute, dataLabelHalfHeight, availableForMarker);

					points.forEach((pt) => {
						pt.dataPointMeta = {
							stat_marker: dataPoint.stat_marker,
							value: dataPoint.value,
							n_size: dataPoint.n_size,
							name: product,
							wave: dataPoint.wave,
						};
					});

					markPointData.push(...points);
				}

				return {
					value,
					originalValue: processedValue,
					n_size: dataPoint.n_size,
					wave: dataPoint.wave,
					attribute,
					productName: product,
					displayValue: formattedVal,
					stat_marker: dataPoint.stat_marker ?? null,
					...(suppressValue ? { label: { color: SUPPRESSED_VALUE_COLOR } } : {}),
					// ...(hasMarker
					// 	? {
					// 			symbolSize: 12,
					// 			itemStyle: {
					// 				color: color || "#fff",
					// 				borderColor: markerBorderColor,
					// 				borderWidth: 1,
					// 			},
					// 			emphasis: {
					// 				itemStyle: {
					// 					borderColor: markerBorderColor,
					// 					borderWidth: 1,
					// 				},
					// 			},
					// 		}
					// 	: {}),
				};
			});

			const hasValidData = seriesData.some((d) => d !== null);
			if (!hasValidData) return;

			series.push({
				name: product,
				type: "line",
				data: seriesData,
				smooth: smoothLine,
				symbol: markerSymbol,
				symbolSize: config.markerSize,
				lineStyle: {
					...(color && { color }),
					width: config.scaledLineWeight,
				},
				itemStyle: {
					...(color && { color, borderColor: color }),
					borderWidth: 2,
				},
				label: {
					show: true,
					position: "right",
					fontSize: LABEL_FONT_SIZE,
					fontWeight: "normal" as const,
					letterSpacing: 0,
					// ...(color && { color }),
					color: "#000",
					formatter: (params: any) => {
						if (!params.data || params.data.value == null) return "";

						const seriesIdx = filteredProducts.indexOf(product);
						const dataIdx = params.dataIndex;
						if (overlapMap.has(`${seriesIdx}-${dataIdx}`)) {
							return "";
						}

						const ds = params.data?.displayValue;
						if (ds === NSizeLabelText.NoData) return SUPPRESSED_VALUE_PLACEHOLDER;
						if (ds === NSizeLabelText.LowN) return "";
						const val = params.data?.value ?? params.value;
						return labelFormat.endsWith("%") ? `${val}%` : val;
					},
				},
				labelLayout: (params: any) => {
					const dataVal = params.data?.value ?? params.value;
					if (typeof dataVal === "number" && dataVal < 0) {
						return { align: "right", dx: -4 };
					}
					return {};
				},
				emphasis: { focus: "series", itemStyle: { borderWidth: 1 } },
				connectNulls: true,
				...(markPointData.length > 0
					? {
						markPoint: {
							symbol: "circle",
							symbolSize: 0,
							data: markPointData,
							animation: false,
							tooltip: { show: true },
						},
					}
					: {}),
			});
		});

		return series;
	}, [filteredProducts, filteredAttributes, productData, productColors, waveMarkers, isPercentage, labelFormat, smoothLine, overlapMap, statTestConfig, width, config]);

	// Classify the whole chart: if no point is plottable (all suppressed) and at least
	// one was a low n-size, surface "Low n" centered instead of the chart (mirrors No-data).
	const dataStatus = useMemo(() => {
		let hasPlottable = false;
		let hasLowN = false;
		filteredProducts.forEach((product) => {
			filteredAttributes.forEach((attr) => {
				const dp = productData[product]?.[attr]?.[0];
				if (!dp || dp.value === null || dp.value === undefined) return;
				const label = getNSizeStatusLabel(dp.n_size);
				if (label === null) hasPlottable = true;
				else if (label === NSizeLabelText.LowN) hasLowN = true;
			});
		});
		if (hasPlottable) return "valid";
		return hasLowN ? "low-n" : "no-data";
	}, [filteredProducts, filteredAttributes, productData]);

	const option = useMemo(
		() => ({
			animation: false,
			grid: {
				left: axisLeft,
				right: hasAnyMarkers ? axisRight + 15 : axisRight,
				top: 0,
				bottom: 0,
				containLabel: false,
			},
			tooltip: {
				...getCommonTooltipConfig(),
				formatter: tooltipFormatter,
				renderMode: "html",
				className: "stat-marker-tooltip",
			},
			xAxis: {
				type: "value",
				min: xAxisDomain.min,
				max: xAxisDomain.max,
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: { show: false },
				splitLine: { show: false },
			},
			yAxis: {
				type: "category",
				data: [...filteredAttributes].reverse(),
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: { show: false },
				boundaryGap: true,
				inverse: false,
			},
			series: echartsSeries,
		}),
		[filteredAttributes, xAxisDomain.min, xAxisDomain.max, echartsSeries, tooltipFormatter, axisRight, axisLeft, hasAnyMarkers],
	);

	// Don't render if no valid data. When every point is suppressed by a low n-size,
	// surface "Low n" centered; otherwise fall back to the No-data placeholder.
	if (echartsSeries.length === 0 || filteredAttributes.length === 0) {
		return (
			<div style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center" }}>
				<span className="text-xs" style={{ color: SUPPRESSED_VALUE_COLOR }}>
					{dataStatus === "low-n" ? NSizeLabelText.LowN : SUPPRESSED_VALUE_PLACEHOLDER}
				</span>
			</div>
		);
	}

	return (
		<div style={{ width, height, pointerEvents: "auto" }}>
			<ReactECharts option={option} ref={chartRef} onEvents={onEvents} style={{ width: "100%", height: "100%" }} notMerge opts={{ renderer: "svg" }} />
		</div>
	);
};

export default React.memo(MiniVLineChart, (prevProps, nextProps) => {
	return (
		prevProps.attributes === nextProps.attributes &&
		prevProps.productData === nextProps.productData &&
		prevProps.productColors === nextProps.productColors &&
		prevProps.waveMarkers === nextProps.waveMarkers &&
		prevProps.visibleProducts === nextProps.visibleProducts &&
		prevProps.visibleWaves === nextProps.visibleWaves &&
		prevProps.xAxisDomain.min === nextProps.xAxisDomain.min &&
		prevProps.xAxisDomain.max === nextProps.xAxisDomain.max &&
		prevProps.labelFormat === nextProps.labelFormat &&
		prevProps.width === nextProps.width &&
		prevProps.height === nextProps.height &&
		prevProps.lineWeight === nextProps.lineWeight &&
		prevProps.smoothLine === nextProps.smoothLine &&
		prevProps.axisRight === nextProps.axisRight &&
		prevProps.axisLeft === nextProps.axisLeft &&
		prevProps.labelOverlapThreshold === nextProps.labelOverlapThreshold &&
		prevProps.statTestConfig === nextProps.statTestConfig
	);
});
